import { RestError } from '@azure/core-http';
import { KnownOrderDtoStatus, OpenLockerResponseDto, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useState } from 'react';
import {
  useMutationCancelOrder,
  useMutationMarkOrderCollected,
  useMutationMarkOrderRetrieved,
  useMutationOpenLocker,
  useQueryCanIOpenOrderLocker
} from '../../order-details.queries';
import { useIsInventoryFlow } from '../../../list/actions/book-order/use-booking-flow-type.hook';

export interface OpenLockerActionProps {
  order: OrderDto;
  onAbort(): void;
  onSuccess(statusChanged: boolean): void;
}

interface OpenLockerActionState {
  error?: RestError;
}

interface IntentToOpen extends OpenLockerActionState {
  current: 'OPEN_REQUEST_INTENT_PENDING';
  loading: boolean;
}

interface OpenRequestFailed extends OpenLockerActionState {
  current: 'OPEN_REQUEST_FAILED';
  response?: OpenLockerResponseDto;
  loading: boolean;
}

interface OpenRequestSuccess extends OpenLockerActionState {
  current: 'OPEN_REQUEST_SUCCESS';
  loading: boolean;
  error?: RestError;
}

export type OpenLockerActionStates = IntentToOpen | OpenRequestFailed | OpenRequestSuccess;

export type ValidOrderStatusChangeOption = Extract<`${KnownOrderDtoStatus}`, 'collected' | 'cancelled' | 'retrieved'>;

export interface OpenLockerActionHandlers {
  onCancelIntent(): void;
  onSendRequest(): void;
  onChangeStatus(): void;
  onStatusInput(status: ValidOrderStatusChangeOption): void;
  onContinueToStatusChange(): void;
  onDone(): void;
}

const initialState: OpenLockerActionStates = {
  current: 'OPEN_REQUEST_INTENT_PENDING',
  loading: false
};

export function useOpenLockerAction({ onAbort, onSuccess, order }: OpenLockerActionProps) {
  const [state, setState] = useState<OpenLockerActionStates>(initialState);
  const [newStatus, setNewStatus] = useState<ValidOrderStatusChangeOption | undefined>();

  // Even if the User has permission to open this locker, they still might not be able to.
  // For example, the locker might contain deliveries for another project.
  const canOpenLocker = useQueryCanIOpenOrderLocker(order.retailer.id, order.id);

  const isLoading = canOpenLocker.isLoading || state.loading;

  const args: [string, string] = [order.retailer.id, order.id];
  const openLockerMutation = useMutationOpenLocker(...args);
  const cancelOrderMutation = useMutationCancelOrder();
  const markOrderCollectedMutation = useMutationMarkOrderCollected();
  const markOrderRetrievedMutation = useMutationMarkOrderRetrieved();
  const showStatusChange = !useIsInventoryFlow(order.retailer.id);

  function getIntentSentState(error?: RestError): IntentToOpen {
    return { current: 'OPEN_REQUEST_INTENT_PENDING', loading: true, error };
  }
  function getOpenRequestErrorState(error: RestError, response?: OpenLockerResponseDto): OpenRequestFailed {
    return { current: 'OPEN_REQUEST_FAILED', loading: false, error, response };
  }

  function getOpenRequestFailedState(response: OpenLockerResponseDto): OpenRequestFailed {
    return { current: 'OPEN_REQUEST_FAILED', loading: false, response };
  }

  function getOpenRequestRetryingState(response?: OpenLockerResponseDto): OpenRequestFailed {
    return { current: 'OPEN_REQUEST_FAILED', loading: true, response };
  }

  function getOpenRequestSuccessState(): OpenRequestSuccess {
    return { current: 'OPEN_REQUEST_SUCCESS', loading: false };
  }

  function getStatusChangeRequestedState(error?: RestError): OpenRequestSuccess {
    return { current: 'OPEN_REQUEST_SUCCESS', loading: true, error };
  }

  function getStatusChangeFailedState(error: RestError): OpenRequestSuccess {
    return { current: 'OPEN_REQUEST_SUCCESS', error, loading: false };
  }

  const handlers: OpenLockerActionHandlers = {
    onCancelIntent() {
      switch (state.current) {
        case 'OPEN_REQUEST_INTENT_PENDING':
        case 'OPEN_REQUEST_FAILED':
          onAbort();
          break;
        default:
          return;
      }
    },
    onSendRequest() {
      switch (state.current) {
        case 'OPEN_REQUEST_INTENT_PENDING':
          setState(getIntentSentState(state.error));
          openLockerMutation.mutate(undefined, {
            onSuccess(response) {
              if (response.status === 'success') {
                // If the project is an inventory flow, don't allow any status change, just show the success state
                if (showStatusChange) {
                  setState(getOpenRequestSuccessState());
                } else {
                  onSuccess(false);
                }
              } else {
                setState(getOpenRequestFailedState(response));
              }
            },
            onError(error) {
              setState(getOpenRequestErrorState(error));
            }
          });
          break;
        case 'OPEN_REQUEST_FAILED':
          setState(getOpenRequestRetryingState(state.response));
          openLockerMutation.mutate(undefined, {
            onSuccess(response) {
              if (response.status === 'success') {
                setState(getOpenRequestSuccessState());
              } else {
                setState(getOpenRequestFailedState(response));
              }
            },
            onError(error) {
              setState(getOpenRequestErrorState(error));
            }
          });
          break;
        default:
          return;
      }
    },
    onStatusInput(newStatus) {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          setNewStatus(newStatus);
          break;
        default:
          return;
      }
    },
    onChangeStatus() {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          if (!newStatus) {
            return;
          }
          setState(getStatusChangeRequestedState(state.error));
          const statusHasChanged = true;

          const mutationOptions = {
            onSuccess() {
              onSuccess(statusHasChanged);
            },
            onError(error: RestError) {
              setState(getStatusChangeFailedState(error));
            }
          };
          if (newStatus === 'cancelled') {
            cancelOrderMutation.mutate({ retailerID: order.retailer.id, orderID: order.id }, mutationOptions);
          }
          if (newStatus === 'collected') {
            markOrderCollectedMutation.mutate({ retailerID: order.retailer.id, orderID: order.id }, mutationOptions);
          }
          if (newStatus === 'retrieved') {
            markOrderRetrievedMutation.mutate({ retailerID: order.retailer.id, orderID: order.id }, mutationOptions);
          }
          break;
        default:
          return;
      }
    },
    onContinueToStatusChange() {
      switch (state.current) {
        case 'OPEN_REQUEST_FAILED':
          setState(getOpenRequestSuccessState());
      }
    },
    onDone() {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          const statusHasChanged = false;
          onSuccess(statusHasChanged);
          break;
        default:
          return;
      }
    }
  };

  return { state, newStatus, handlers, isLoading, canOpenLocker: canOpenLocker.data } as const;
}
